import { toPoint } from "mgrs";

import type { BBox, LonLat } from "../../api/types";
import { k } from "../../i18n/i18n";

/** Recherche de lieu de la zone libre (docs/14 §2.7, point 2) : Mapbox Geocoding v6 depuis le navigateur, avec le
 *  jeton public de la carte ; MGRS et coordonnées décimales reconnus sans requête. Trois caractères au moins,
 *  requête partie 300 ms après la dernière frappe, la précédente annulée, réponses gardées pour la session. Les
 *  coordonnées du géocodeur ne sont jamais enregistrées : le choix d'un lieu centre la carte et préremplit le nom de
 *  la zone. */

export const MIN_QUERY = 3;
export const DEBOUNCE_MS = 300;
export const MAX_RESULTS = 6;
const GEOCODER = "https://api.mapbox.com/search/geocode/v6/forward";

export type PlaceGroup = "coords" | "places" | "regions" | "addresses";
/** Titres des groupes de résultats, dans l'ordre d'affichage. */
export const GROUP_LABELS: Readonly<Record<PlaceGroup, string>> = {
  coords: k("Coordonnées"),
  places: k("Lieux"),
  regions: k("Régions et pays"),
  addresses: k("Adresses"),
};
const GROUP_ORDER: readonly PlaceGroup[] = ["coords", "places", "regions", "addresses"];

export interface PlaceResult {
  id: string;
  /** Nom du lieu (préremplit le nom de la zone). */
  name: string;
  /** Région, pays (seconde ligne). */
  detail: string;
  group: PlaceGroup;
  center: LonLat;
  /** Emprise du lieu : la carte se centre sur son milieu. */
  bbox: BBox | null;
}

export type ParsedQuery =
  | { kind: "short" }
  | { kind: "text"; text: string }
  | { kind: "point"; source: "mgrs" | "coords"; result: PlaceResult };

const MGRS = /^(\d{1,2})\s*([C-HJ-NP-X])\s*([A-HJ-NP-Z]{2})\s*(\d+)?\s*(\d+)?$/i;

/** MGRS (« 35V MF 12345 67890 », « 35VMF1234567890 », 0 à 5 chiffres par coordonnée) : [lon, lat] du centre de la
 *  case, ou null. */
export function parseMgrs(raw: string): LonLat | null {
  const m = MGRS.exec(raw.trim());
  if (!m) return null;
  const zone = Number(m[1]);
  if (zone < 1 || zone > 60) return null;
  const digits = `${m[4] ?? ""}${m[5] ?? ""}`;
  if (m[4] && m[5] && m[4].length !== m[5].length) return null;
  if (digits.length % 2 !== 0 || digits.length > 10) return null;
  try {
    const [lon, lat] = toPoint(`${zone}${m[2]!.toUpperCase()}${m[3]!.toUpperCase()}${digits}`);
    return Number.isFinite(lon) && Number.isFinite(lat) ? [lon, lat] : null;
  } catch {
    return null;
  }
}

const NUMBER = String.raw`[-+−]?\d+(?:[.,]\d+)?`;
const HEMI = String.raw`[NSEOW]`;
// Deux nombres décimaux (séparés par une virgule, un point-virgule ou des espaces), avec ou sans lettre
// d'hémisphère avant ou après (« 59.3, 24.5 », « 59,3 ; 24,5 », « 59.3N 24.5E », « N 59.3 E 24.5 »).
const COORDS = new RegExp(
  String.raw`^(${HEMI})?\s*(${NUMBER})\s*°?\s*(${HEMI})?\s*(?:[,;]|\s)\s*(${HEMI})?\s*(${NUMBER})\s*°?\s*(${HEMI})?$`,
  "i",
);

/** Coordonnées décimales [lon, lat], ou null. Sans lettre d'hémisphère, la latitude vient d'abord ; une lettre E,
 *  O ou W devant le premier nombre (ou N, S devant le second) les inverse. Virgule ou point décimal. */
export function parseCoordinates(raw: string): LonLat | null {
  const m = COORDS.exec(raw.trim());
  if (!m) return null;
  const number = (s: string) => Number(s.replace("−", "-").replace(",", "."));
  const hemiA = (m[1] ?? m[3])?.toUpperCase();
  const hemiB = (m[4] ?? m[6])?.toUpperCase();
  const signed = (h: string | undefined, v: number) => (h === "S" || h === "O" || h === "W" ? -Math.abs(v) : v);
  const a = signed(hemiA, number(m[2]!));
  const b = signed(hemiB, number(m[5]!));
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const lonFirst = hemiA === "E" || hemiA === "O" || hemiA === "W" || hemiB === "N" || hemiB === "S";
  const [lat, lon] = lonFirst ? [b, a] : [a, b];
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return [lon, lat];
}

const fixed = (v: number) => v.toFixed(5).replace(/\.?0+$/, "");

/** Analyse de la saisie : trop courte, point (MGRS ou coordonnées, sans requête) ou texte à géocoder. */
export function parseQuery(raw: string): ParsedQuery {
  const text = raw.trim().replace(/\s+/g, " ");
  const mgrs = parseMgrs(text);
  if (mgrs) {
    return {
      kind: "point",
      source: "mgrs",
      result: { id: `mgrs:${text}`, name: text.toUpperCase(), detail: `${fixed(mgrs[1])}, ${fixed(mgrs[0])}`, group: "coords", center: mgrs, bbox: null },
    };
  }
  const coords = parseCoordinates(text);
  if (coords) {
    return {
      kind: "point",
      source: "coords",
      result: { id: `coords:${text}`, name: `${fixed(coords[1])}, ${fixed(coords[0])}`, detail: "", group: "coords", center: coords, bbox: null },
    };
  }
  return text.length < MIN_QUERY ? { kind: "short" } : { kind: "text", text };
}

/** Adresse de la requête au géocodeur (six résultats au plus, dans la langue de l'interface). */
export function geocodeUrl(text: string, token: string, language: "fr" | "en"): string {
  const params = new URLSearchParams({
    q: text,
    access_token: token,
    limit: String(MAX_RESULTS),
    language,
    autocomplete: "true",
  });
  return `${GEOCODER}?${params}`;
}

const TYPE_GROUP: Readonly<Record<string, PlaceGroup>> = {
  country: "regions",
  region: "regions",
  district: "places",
  place: "places",
  locality: "places",
  neighborhood: "places",
  postcode: "addresses",
  street: "addresses",
  address: "addresses",
  block: "addresses",
  secondary_address: "addresses",
};

interface GeocodeFeature {
  id?: string;
  geometry?: { coordinates?: unknown };
  properties?: {
    mapbox_id?: string;
    feature_type?: string;
    name?: string;
    name_preferred?: string;
    place_formatted?: string;
    full_address?: string;
    bbox?: unknown;
    coordinates?: { longitude?: number; latitude?: number };
  };
}

const isBox = (v: unknown): v is BBox => Array.isArray(v) && v.length === 4 && v.every((n) => typeof n === "number" && Number.isFinite(n));

/** Résultats lisibles d'une réponse du géocodeur v6 (les entrées incomplètes sont écartées). */
export function parseGeocode(json: unknown): PlaceResult[] {
  const features = (json as { features?: GeocodeFeature[] } | null)?.features;
  if (!Array.isArray(features)) return [];
  const out: PlaceResult[] = [];
  for (const f of features) {
    const p = f.properties ?? {};
    const coords = p.coordinates;
    const geometry = Array.isArray(f.geometry?.coordinates) ? (f.geometry!.coordinates as unknown[]) : null;
    const lon = coords?.longitude ?? (typeof geometry?.[0] === "number" ? geometry[0] : undefined);
    const lat = coords?.latitude ?? (typeof geometry?.[1] === "number" ? geometry[1] : undefined);
    const name = p.name_preferred || p.name;
    if (typeof lon !== "number" || typeof lat !== "number" || !name) continue;
    out.push({
      id: p.mapbox_id || f.id || `${name}:${lon},${lat}`,
      name,
      detail: p.place_formatted ?? "",
      group: TYPE_GROUP[p.feature_type ?? ""] ?? "places",
      center: [lon, lat],
      bbox: isBox(p.bbox) ? p.bbox : null,
    });
    if (out.length >= MAX_RESULTS) break;
  }
  return out;
}

/** Résultats rangés par groupe (coordonnées, lieux, régions et pays, adresses), dans l'ordre d'affichage ; l'ordre
 *  du géocodeur est gardé dans chaque groupe. */
export function groupResults(results: readonly PlaceResult[]): { group: PlaceGroup; items: PlaceResult[] }[] {
  return GROUP_ORDER.map((group) => ({ group, items: results.filter((r) => r.group === group) })).filter((g) => g.items.length > 0);
}

/** Point où centrer la carte : milieu de l'emprise du lieu, sinon son point. */
export function placeCenter(result: PlaceResult): LonLat {
  if (result.bbox) return [(result.bbox[0] + result.bbox[2]) / 2, (result.bbox[1] + result.bbox[3]) / 2];
  return result.center;
}

/** Zoom d'arrivée : d'après la taille de l'emprise du lieu (un pays de loin, une localité de près). */
export function placeZoom(result: PlaceResult): number {
  if (!result.bbox) return 10;
  const span = Math.max(result.bbox[2] - result.bbox[0], result.bbox[3] - result.bbox[1]);
  return Math.max(3, Math.min(12, Math.log2(360 / Math.max(span, 0.01)) + 0.5));
}

/** Zoom d'arrivée sur le lieu nommé d'un nouveau terrain (vue Manœuvre, « Tracer l'axe », « Recentrer ») : la
 *  localité elle-même, nettement lisible, quel que soit le niveau (demande d'Alexis du 02/11/2026 : « le zoom est trop
 *  éloigné ») ; on dézoome ensuite pour tracer un long axe. */
export const PLACE_ZOOM: Readonly<Record<string, number>> = {
  COMPAGNIE: 13,
  REGIMENT: 12,
  BRIGADE: 12,
  DIVISION: 12,
  CORPS: 12,
};

/** Zoom d'arrivée sur un lieu nommé pour un niveau (régiment par défaut). */
export function placeZoomFor(level: string | null | undefined): number {
  return PLACE_ZOOM[level ?? ""] ?? PLACE_ZOOM.REGIMENT!;
}

/** Distance au-delà de laquelle le terrain placé est « loin » du lieu nommé (celle du serveur, `PLACE_FAR_KM`) : la
 *  météo choisie y est revue pour le terrain placé. */
export const PLACE_FAR_KM = 150;

/** Distance du grand cercle entre deux points [lon, lat], en kilomètres. */
export function distanceKm(a: LonLat, b: LonLat): number {
  const rad = Math.PI / 180;
  const dLat = (b[1] - a[1]) * rad;
  const dLon = (b[0] - a[0]) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * rad) * Math.cos(b[1] * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Distance du lieu nommé au centre de l'axe tracé (le point dont le serveur revoit la météo), en kilomètres. */
export function axisDistanceKm(axis: { start: LonLat; end: LonLat }, place: { lon: number; lat: number }): number {
  const center: LonLat = [(axis.start[0] + axis.end[0]) / 2, (axis.start[1] + axis.end[1]) / 2];
  return distanceKm(center, [place.lon, place.lat]);
}

export type SearchState =
  | { status: "idle" }
  | { status: "short" }
  | { status: "loading"; text: string }
  | { status: "done"; text: string; results: PlaceResult[] }
  | { status: "unavailable"; text: string };

/** Moteur de recherche avec délai, annulation et mémoire de session ; `fetchImpl` et les minuteries sont injectés
 *  pour les tests. `onState` reçoit chaque état. Sans jeton (carte MapLibre), le texte libre est « indisponible ». */
export class PlaceSearcher {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private controller: AbortController | null = null;
  private readonly cache = new Map<string, PlaceResult[]>();
  private seq = 0;

  constructor(
    private readonly options: {
      token: string;
      language: () => "fr" | "en";
      onState: (state: SearchState) => void;
      fetchImpl?: typeof fetch;
      delay?: number;
    },
  ) {}

  /** Nouvelle saisie : un point (MGRS, coordonnées) répond aussitôt ; un texte part après le délai. */
  input(raw: string): void {
    this.cancel();
    const parsed = parseQuery(raw);
    if (!raw.trim()) return this.options.onState({ status: "idle" });
    if (parsed.kind === "point") return this.options.onState({ status: "done", text: raw.trim(), results: [parsed.result] });
    if (parsed.kind === "short") return this.options.onState({ status: "short" });
    const key = `${this.options.language()}|${parsed.text.toLowerCase()}`;
    const cached = this.cache.get(key);
    if (cached) return this.options.onState({ status: "done", text: parsed.text, results: cached });
    if (!this.options.token) return this.options.onState({ status: "unavailable", text: parsed.text });
    this.options.onState({ status: "loading", text: parsed.text });
    const seq = ++this.seq;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.run(parsed.text, key, seq);
    }, this.options.delay ?? DEBOUNCE_MS);
  }

  private async run(text: string, key: string, seq: number): Promise<void> {
    const controller = new AbortController();
    this.controller = controller;
    try {
      const fetchImpl = this.options.fetchImpl ?? fetch;
      const res = await fetchImpl(geocodeUrl(text, this.options.token, this.options.language()), { signal: controller.signal });
      if (!res.ok) throw new Error(String(res.status));
      const results = parseGeocode(await res.json());
      this.cache.set(key, results);
      if (seq === this.seq) this.options.onState({ status: "done", text, results });
    } catch (error) {
      if ((error as { name?: string })?.name === "AbortError" || controller.signal.aborted) return;
      if (seq === this.seq) this.options.onState({ status: "unavailable", text });
    } finally {
      if (this.controller === controller) this.controller = null;
    }
  }

  /** Saisie effacée ou remplacée : délai en cours abandonné, requête en vol annulée. */
  cancel(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.controller?.abort();
    this.controller = null;
    this.seq++;
  }
}
